import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import {
  getBranchOptions,
  getDeclaredFields,
  getDeviceById,
  getInternalQuotation,
  getPhoneModelOptions,
} from '@/features/operations/api';
import { DeviceSheet } from '@/features/operations/components/DeviceSheet';
import { buildInspectionFields } from '@/features/operations/condition';
import { formatQuotationDate, originLabels } from '@/features/operations/labels';
import type {
  BranchOption,
  DeviceDetail,
  DeviceModelOption,
  InspectionFieldSummary,
  QuotationRequestItem,
} from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

type DetailLocationState = {
  /// The list's query string, so the way back returns to the same filters.
  from?: string;
  /// Set right after a direct entry, so the page says the phone was registered.
  justCreated?: boolean;
} | null;

/**
 * One received phone: its internal sheet and who offered it. Nothing of the quotation is shown
 * here -- that lives in the quotation's own detail, one link away.
 */
export function DeviceDetailPage() {
  const { token, user } = useAuth();
  const { id = '' } = useParams();
  const location = useLocation();
  const locationState = location.state as DetailLocationState;
  const backTo = `/admin/devices${locationState?.from ? `?${locationState.from}` : ''}`;
  const [device, setDevice] = useState<DeviceDetail | null>(null);
  // Loaded only so the sheet can compare the observed condition with the declared one; none of
  // it is rendered on this page.
  const [quotation, setQuotation] = useState<QuotationRequestItem | null>(null);
  const [modelOptions, setModelOptions] = useState<DeviceModelOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [inspectionFields, setInspectionFields] = useState<InspectionFieldSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    locationState?.justCreated ? 'Equipo ingresado.' : null,
  );

  useEffect(() => {
    const load = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [item, models, branches, fields] = await Promise.all([
          getDeviceById(token, id),
          getPhoneModelOptions(token),
          getBranchOptions(token),
          getDeclaredFields(),
        ]);
        const request = item.quotation
          ? await getInternalQuotation(token, item.quotation.id)
          : null;

        setDevice(item);
        setQuotation(request);
        setModelOptions(models);
        setBranchOptions(branches);
        setInspectionFields(buildInspectionFields(fields));
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar el equipo.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token, id]);

  if (!token) {
    return null;
  }

  const backLink = (
    <Box>
      <Button component={RouterLink} to={backTo} color="inherit">
        ← Volver a equipos
      </Button>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!device) {
    return (
      <Stack spacing={3}>
        {backLink}
        <Alert severity="error">{errorMessage || 'No encontramos el equipo.'}</Alert>
      </Stack>
    );
  }

  const handleDeviceUpdated = (updated: DeviceDetail, message?: string) => {
    setDevice(updated);
    setErrorMessage(null);
    setSuccessMessage(message ?? 'Ficha del equipo actualizada.');
  };

  const sourceQuotation = device.quotation;

  return (
    <Stack spacing={3}>
      {backLink}
      <PageHeader
        eyebrow="EQUIPO"
        title={`${device.brand} ${device.model}`}
        description={[
          device.storageGb === null ? null : `${device.storageGb}GB`,
          device.color?.name,
          `Recibido el ${formatQuotationDate(device.createdAt)}`,
          device.branch ? `${device.branch.name} · ${device.branch.city}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        badge={<StatusChip domain="device" status={device.deviceState} size="medium" />}
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <DeviceSheet
                token={token}
                device={device}
                quotation={quotation}
                configuredFields={inspectionFields}
                modelOptions={modelOptions}
                branchOptions={branchOptions}
                canRevertSale={user?.role === 'ADMIN'}
                onUpdated={handleDeviceUpdated}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard
            title="Dueño"
            description="Quien ofreció el equipo, tal como lo registró su cotización."
          >
            {sourceQuotation ? (
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {sourceQuotation.contact.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {sourceQuotation.contact.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Teléfono: {sourceQuotation.contact.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cotizó por: {originLabels[sourceQuotation.origin] || sourceQuotation.origin}
                </Typography>
                <Link
                  component={RouterLink}
                  to={`/admin/quotations/${sourceQuotation.id}`}
                  variant="body2"
                  underline="hover"
                >
                  Ver cotización de origen
                </Link>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {device.origin === 'DIRECT_ENTRY'
                  ? 'Ingreso directo: no hay dueño registrado.'
                  : 'Sin cotización (registro anterior): no hay dueño registrado.'}
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  );
}
